import { useTaskDueAndTimeslot } from '@griply/hooks';
import {
  DueDisplayOptions,
  ID,
  initialDateFormat,
  LinkOptions,
  Task,
  Timestamp,
  UpdateTask,
} from '@griply/types';
import {
  getHabitEndedDate,
  getHabitEndedLabel,
  getTaskRescheduleUpdate,
  getTimeFormat,
  getWeekStartsOn,
  primaryGoalId,
  primaryLifeAreaId,
} from '@griply/utils';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useTaskTodayControls } from 'features/tasks';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GoalLinkPicker } from 'shared/components/connected/goal-link-picker';
import {
  Colors,
  TaskMetaInteraction,
} from 'shared/components/ui/task-meta-interaction';
import { TaskMetaInteractionDueDate } from 'shared/components/ui/task-meta-interaction-due-date';
import { TaskMetaInteractionLinkedItem } from 'shared/components/ui/task-meta-interaction-linked-item';
import { TaskMetaInteractionTimeslot } from 'shared/components/ui/task-meta-interaction-timeslot';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';

import { TaskMetaInteractionTags } from './task-meta-interaction-tags';
import * as Styled from './task-meta-interactions.style';

const allDueDisplayOptions = Object.values(DueDisplayOptions);

export type TaskMetaInteractionsProps = {
  task: Task;
  onUpdate?: (task: Task, updates: UpdateTask) => void;
  displayDate?: Timestamp;
  displayDue?: DueDisplayOptions[];
  hasTooltips?: boolean;
  hideGoal?: boolean;
  hideLifeArea?: boolean;
  showGoalColor?: boolean;
  highlight?: boolean;
  preventInteraction?: boolean;
  isMobileOutlined?: boolean;
  isFaded?: boolean;
  tagIds?: ID[];
  autoWidth?: boolean;
};

export const TaskMetaInteractions: React.FC<TaskMetaInteractionsProps> = memo(
  ({
    task,
    onUpdate,
    displayDate,
    displayDue = allDueDisplayOptions,
    hideGoal,
    hideLifeArea,
    showGoalColor,
    highlight,
    hasTooltips,
    preventInteraction,
    isMobileOutlined,
    isFaded,
    tagIds,
    autoWidth,
  }) => {
    const { t } = useTranslation();
    const user = useUser();
    const today = useToday();
    const goals = useGoals();
    const lifeAreas = useLocalizedLifeAreas();

    const endedLabel = useMemo(
      () =>
        getHabitEndedLabel(getHabitEndedDate(task.schedules ?? [], today), t),
      [task.schedules, today, t],
    );

    const { onUpdate: onUpdateGlobal } = useTaskTodayControls();
    const handleUpdate = onUpdate ?? onUpdateGlobal;

    // prevent edit mode on the task-card
    const onItemContainer = (e: React.MouseEvent) => e.stopPropagation();

    const updateDeadline = (date?: Timestamp) => {
      if (task.frozenAt) {
        return;
      }

      handleUpdate(task, {
        id: task.id,
        endStrategy: {
          deadline: date ?? null,
          completionCount: task.endStrategy?.completionCount ?? null,
        },
      });
    };

    const onChangeGoal = (link: { id: ID; type: LinkOptions } | null) =>
      !task.frozenAt &&
      handleUpdate(task, {
        id: task.id,
        lifeAreaId: link?.type === LinkOptions.LifeArea ? link.id : null,
        goalId: link?.type === LinkOptions.Goal ? link.id : null,
      });

    const dateFormat = user?.settings?.dateFormat ?? initialDateFormat;
    const weekStartsOn = getWeekStartsOn(user);

    const { dueDate, timeslot, hasTimeslot } = useTaskDueAndTimeslot(task, {
      today,
      weekStartsOn,
      displayDate,
      displayDue,
    });

    const rescheduleOccurrence = (date?: Timestamp) => {
      if (!!task.frozenAt || !date) {
        return;
      }

      handleUpdate(
        task,
        getTaskRescheduleUpdate(task, date, timeslot ?? null, {
          today,
          weekStartsOn,
          originalDate: displayDate ?? dueDate,
        }),
      );
    };

    const linkedGoalId = primaryGoalId(task);
    const linkedLifeAreaId = primaryLifeAreaId(task);
    const linkedItemId =
      (hideGoal && linkedGoalId) || (hideLifeArea && linkedLifeAreaId)
        ? undefined
        : linkedGoalId ?? linkedLifeAreaId;

    return (
      <Styled.List
        $preventInteraction={!!preventInteraction}
        $isMobileOutlined={!!isMobileOutlined}
        $isFaded={!!isFaded}
        $autoWidth={!!autoWidth}
      >
        {!!endedLabel && (
          <Styled.ListItem $hasValue $isMobileOutlined={!!isMobileOutlined}>
            <TaskMetaInteraction
              color={Colors.Error}
              label={endedLabel}
              isMobileOutlined={isMobileOutlined}
            />
          </Styled.ListItem>
        )}

        {(!!dueDate || hasTimeslot) && (
          <Styled.ListItem
            onClick={onItemContainer}
            $hasValue={!!dueDate || hasTimeslot}
            $isMobileOutlined={!!isMobileOutlined}
          >
            {!!dueDate && (
              <TaskMetaInteractionDueDate
                dateFormat={dateFormat}
                weekStartsOn={weekStartsOn}
                onChange={
                  task.schedules ? rescheduleOccurrence : updateDeadline
                }
                isFrozen={!!task.frozenAt}
                value={dueDate}
                hasTimeslot={hasTimeslot}
                highlight={highlight}
                hasTooltip={hasTooltips}
                isDeadline
                canRemove={!task.schedules}
                isMobileOutlined={isMobileOutlined}
                tomorrowLabelAllowed
                isCompleted={!!task.completedAt}
              />
            )}
            {hasTimeslot && (
              <TaskMetaInteractionTimeslot
                task={task}
                timeFormat={getTimeFormat(user)}
                isFrozen={!!task.frozenAt}
                value={timeslot}
                representedDay={displayDate ?? today}
                hasDueDate={!!dueDate}
                hasTooltip={hasTooltips}
                isMobileOutlined={isMobileOutlined}
              />
            )}
          </Styled.ListItem>
        )}

        {!!linkedItemId && (
          <Styled.ListItem
            onClick={onItemContainer}
            $hasValue={!!linkedItemId}
            $isMobileOutlined={!!isMobileOutlined}
          >
            <TaskMetaInteractionLinkedItem
              LinkPicker={GoalLinkPicker}
              goals={goals}
              lifeAreas={lifeAreas}
              value={linkedItemId}
              onChange={onChangeGoal}
              isFrozen={!!task.frozenAt}
              showGoalColor={showGoalColor}
              highlight={highlight}
              hasTooltip={hasTooltips}
              isMobileOutlined={isMobileOutlined}
            />
          </Styled.ListItem>
        )}

        <TaskMetaInteractionTags
          id={task.id}
          isMobileOutlined={isMobileOutlined}
          tagIds={tagIds}
        />
      </Styled.List>
    );
  },
);
